/**
 * StoreWaitlist — public landing shown at /store while the catalog is
 * gated behind the store_v1 feature flag. Captures email + optional
 * category interests so launch outreach has a warm list ready.
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { ShoppingBag, CheckCircle2, Package, Wrench, Boxes, Coffee } from 'lucide-react';

const INTERESTS = [
  { key: 'trays',      label: 'Trays + liners',     icon: Boxes },
  { key: 'bags',       label: 'Mylar + O₂',         icon: Package },
  { key: 'machines',   label: 'Freeze dryers',      icon: Wrench },
  { key: 'ingredients',label: 'Bulk ingredients',   icon: Coffee },
];

export default function StoreWaitlist() {
  const { API, user } = useContext(AuthContext);
  const [email, setEmail] = useState(user?.email || '');
  const [note, setNote] = useState('');
  const [interests, setInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(null);

  const toggle = (key) => setInterests((v) => (v.includes(key) ? v.filter((k) => k !== key) : [...v, key]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter an email'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/store/waitlist`, {
        email: email.trim(),
        interests,
        note: note.trim() || null,
      });
      setJoined(true);
      setCount(data?.waitlist_count || null);
      toast.success("You're on the list — we'll email you when it opens");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="store-waitlist-page">
      <div className="container mx-auto px-6 py-16 sm:py-24 max-w-2xl">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mb-4">
            <ShoppingBag className="w-7 h-7" />
          </div>
          <div className="eyebrow text-primary">Coming soon</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">
            The FDAcademy Store is <span className="text-amber-brand">stocking shelves.</span>
          </h1>
          <p className="mt-4 text-base text-foreground/75 leading-relaxed">
            Trays, mylar, O₂ absorbers, machines and bulk product we actually trust and use in our own runs. Drop your email and we'll ping you the minute it opens.
          </p>
        </div>

        {joined ? (
          <div className="mt-10 pro-card p-8 text-center border-2 border-emerald-500/40 bg-emerald-500/5" data-testid="store-waitlist-joined">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
            <h2 className="font-heading text-2xl font-extrabold mt-3">You're on the list</h2>
            <p className="mt-2 text-sm text-foreground/75">
              We'll email <b>{email}</b> the moment the store goes live.
              {typeof count === 'number' && <> You're #{count} in the queue.</>}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 pro-card p-6 sm:p-8 space-y-4" data-testid="store-waitlist-form">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 w-full rounded-lg border border-border bg-background px-4 py-3 text-sm"
                data-testid="store-waitlist-email"
              />
            </label>

            <div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">What are you shopping for?</span>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {INTERESTS.map((i) => {
                  const on = interests.includes(i.key);
                  const Icon = i.icon;
                  return (
                    <button
                      key={i.key}
                      type="button"
                      onClick={() => toggle(i.key)}
                      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold text-left transition-colors ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground hover:border-primary/50'}`}
                      data-testid={`store-waitlist-interest-${i.key}`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {i.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Anything specific you're hunting for?</span>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. wide-mouth 5-mil mylar, medium freeze dryer"
                className="mt-1 w-full rounded-lg border border-border bg-background px-4 py-2 text-sm"
                data-testid="store-waitlist-note"
              />
            </label>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 h-12"
              data-testid="store-waitlist-submit"
            >
              {submitting ? 'Adding you…' : 'Notify me when it opens'}
            </Button>
            <p className="text-[10px] text-muted-foreground italic text-center">
              We'll never sell your email. One launch note, that's it.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
